import { slugify } from '../map/slugify';
import { EntitreeNode, EntitreeMap, Pass1LearningPath, TopicNodePass2, Pass2LearningPath, EntitreeData } from '../../typesV2';

/**
 * Converts a learning path array into an Entitree flex compatible format
 * This function is used for the first pass and doesn't handle sideQuests
 * @param learningPath The learning path array to convert
 * @returns An object in Entitree flex format
 */
export function convertLearningPathToEntitreeFormat(
  learningPath: Pass1LearningPath | Pass2LearningPath
): EntitreeMap {
  const result: EntitreeMap = {};
  const idToKeyMap: Record<string, number> = {}; // Maps node IDs to their numeric keys
  let nextKey = 1; // Start keys from 1
  let wasLastNodeOnLeft = false;
  let coreTopicNumber = 0;
  // First pass: create all nodes with numeric keys and build the ID to key mapping
  learningPath.forEach((topic) => {
    const topicId = topic?.topicTitle ? slugify(topic?.topicTitle) : '';

    // Assign a numeric key to this topic
    const topicKey = nextKey++;
    idToKeyMap[topicId] = topicKey;

    // Create the base node for the current topic
    const node: EntitreeData = {
      id: topicId,
      name: topic.topicTitle,
      topicTitle: topic.topicTitle,
      topicDescription: topic.topicDescription,
      sections: topic.sections,
      options: topic.options,
      type: topic.type || 'input', // Default to 'input' if type is not specified
      nodeOnLeft: !wasLastNodeOnLeft,
      coreTopicNumber: coreTopicNumber,
    };
    coreTopicNumber++;

    // Add the node to the result with numeric key (as integer, not string)
    result[topicKey] = node;
    if (topic.type === 'normal') wasLastNodeOnLeft = !wasLastNodeOnLeft;
  });

  // Second pass: set up children references using numeric keys
  learningPath.forEach((topic, index) => {
    const topicId = topic?.topicTitle ? slugify(topic?.topicTitle) : '';
    const topicKey = idToKeyMap[topicId];

    // Add children if this isn't the last topic
    if (index < learningPath.length - 1) {
      const nextTopicId = learningPath[index + 1]?.topicTitle
        ? slugify(learningPath[index + 1]?.topicTitle)
        : '';
      const nextTopicKey = idToKeyMap[nextTopicId];
      result[topicKey].children = [nextTopicKey.toString()]; // Convert to string for the array
    }
  });

  return result;
}

/**
 * Updates an existing Entitree data structure with embellished learning path data
 * @param currentEntitreeMap The current Entitree data structure
 * @param embellishedLearningPath The embellished learning path with additional data
 * @returns An updated Entitree data structure with embellished data and side quests
 */
export function updateEntitreeWithEmbellishedData(
  currentEntitreeMap: EntitreeMap,
  embellishedLearningPath: Pass2LearningPath,
): EntitreeMap {  

  // Create a copy of the current data to avoid mutating the original
  const currentLearningPath: EntitreeMap = { ...currentEntitreeMap };

  // Keep track of the highest key used so far
  let nextKey = 1;
  for (const key in currentLearningPath) {
    const numKey = parseInt(key);
    if (numKey >= nextKey) {
      nextKey = numKey + 1;
    }
  }
  // Map topic IDs to their keys in the current data
  const idToKeyMap: Record<string, string> = {};
  for (const key in currentLearningPath) {
    const node = currentLearningPath[key];
    idToKeyMap[node.id] = key;
  }

  // Process each topic in the embellished learning path
  embellishedLearningPath.forEach((topic) => {
    const topicId = topic?.topicTitle ? slugify(topic?.topicTitle) : '';

    // Check if this topic already exists in the current data
    if (idToKeyMap[topicId]) {
      const topicKey = idToKeyMap[topicId];

      // If topic types are different, completely replace the current topic
      // if (topic.type && currentLearningPath[topicKey].type && topic.type !== currentLearningPath[topicKey].type) {
        currentLearningPath[topicKey] = {
          ...currentLearningPath[topicKey],
          ...topic,
          topicTitle: currentLearningPath[topicKey].topicTitle,
          topicDescription: currentLearningPath[topicKey].topicDescription,
        };
      // } else {
        // merge the options
        // new values replace existing
        // cresultinging array will not be longer than the original (new options from pass2 will be omitted)
      //   currentLearningPath[topicKey].options = topic.options && currentLearningPath[topicKey]?.options ? 
      //     currentLearningPath[topicKey].options.map((existingOption, index) => ({
      //       ...existingOption,
      //       ...(topic.options && topic.options[index] ? topic.options[index] : {})
      //     })).slice(0, currentLearningPath[topicKey].options.length) : 
      //     topic.options
      // }

      // Process side quests if any
      if (topic.sideQuests && topic.sideQuests.length > 0) {
        // Create a single key for all sidequests of this topic
        const sideQuestKey = nextKey++;
        const sideQuestId = `${topicId}-sidequests`;
        

        if (currentLearningPath[topicKey].nodeOnLeft) {
          // Add the consolidated sidequest key to siblings array
          currentLearningPath[topicKey].spouses = [sideQuestKey.toString()];

          // Create a single entry containing all sidequests for this topic
          currentLearningPath[sideQuestKey] = {
            id: sideQuestId,
            name: `${topic.topicTitle} Side Quests`,
            isSpouse: true,
            topicTitle: topic.topicTitle,
            topicDescription: topic.topicDescription,
            type: 'sideQuest',
            parentId: topicKey,
            sideQuests: topic.sideQuests,
          };
        } else {
          currentLearningPath[topicKey].siblings = [sideQuestKey.toString()];

          // Create a single entry containing all sidequests for this topic
          currentLearningPath[sideQuestKey] = {
            id: sideQuestId,
            name: `${topic.topicTitle} Side Quests`,
            isSibling: true,
            topicTitle: topic.topicTitle,
            topicDescription: topic.topicDescription,
            type: 'sideQuest',
            parentId: topicKey,
            sideQuests: topic.sideQuests,
          };
        }
      }
    }
  });

  return currentLearningPath;
}
